import { getLogger } from "@/utils/logger";

const log = getLogger("VisitorTrackingService");

// Generate a visitor ID and store in localStorage
const generateVisitorId = () => {
  const randomId = Math.random().toString(36).substring(2, 15);
  const timestamp = Date.now().toString(36);
  return `v_${randomId}${timestamp}`;
};

class VisitorTrackingService {
  constructor() {
    this.visitorId = this.getOrCreateVisitorId();
    this.sessionId = this.createSessionId();
    this.apiEndpoint = `${import.meta.env.VITE_SERVER_URL}/visitors/track`;
    this.currentPage = window.location.pathname;
    this.lastActivity = Date.now();
    this.isTracking = false;
    this.heartbeatInterval = null;
    this.beforeUnloadHandler = this.handleBeforeUnload.bind(this);
    this.lastTrackedPage = null;
    this.lastPageViewAt = 0;
    this.pageViewDebounceMs = this.parseDebounceMs(import.meta.env.VITE_VISITOR_TRACKING_DEBOUNCE_MS);
    this.sampleRate = this.parseSampleRate(import.meta.env.VITE_VISITOR_TRACKING_SAMPLE_RATE, 1);
    this.automationSampleRate = this.parseSampleRate(
      import.meta.env.VITE_VISITOR_TRACKING_AUTOMATION_SAMPLE_RATE,
      this.sampleRate
    );
    this.isAutomationSession = Boolean(
      typeof navigator !== "undefined" && navigator.webdriver
    );
  }

  parseDebounceMs(value) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed;
    }
    return 750;
  }

  parseSampleRate(value, fallback) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return Math.max(0, Math.min(1, parsed));
    }
    return fallback;
  }

  getEffectiveSampleRate() {
    return this.isAutomationSession ? this.automationSampleRate : this.sampleRate;
  }

  shouldSendEvent() {
    const effectiveSampleRate = this.getEffectiveSampleRate();
    if (effectiveSampleRate >= 1) {
      return true;
    }
    if (effectiveSampleRate <= 0) {
      return false;
    }
    return Math.random() < effectiveSampleRate;
  }

  getOrCreateVisitorId() {
    try {
      let visitorId = localStorage.getItem('landivo_visitor_id');
      if (!visitorId) {
        visitorId = generateVisitorId();
        localStorage.setItem('landivo_visitor_id', visitorId);
      }
      return visitorId;
    } catch (e) {
      return generateVisitorId();
    }
  }

  createSessionId() {
    return `s_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  startTracking() {
    if (this.isTracking) {
      return;
    }
    this.isTracking = true;

    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    // Heartbeat every 5 minutes while tracking is active.
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, 5 * 60 * 1000);
  }

  stopTracking() {
    if (!this.isTracking) {
      return;
    }
    this.isTracking = false;

    window.removeEventListener('beforeunload', this.beforeUnloadHandler);

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  trackPageView(page = window.location.pathname, options = {}) {
    const normalizedPage = typeof page === "string" ? page : window.location.pathname;
    const force = Boolean(options.force);
    const now = Date.now();

    // Ensure one page-view event per route transition and debounce duplicates.
    if (!force) {
      if (this.lastTrackedPage === normalizedPage) {
        return;
      }
      if (now - this.lastPageViewAt < this.pageViewDebounceMs) {
        return;
      }
    }

    this.currentPage = normalizedPage;
    this.lastActivity = Date.now();
    this.lastTrackedPage = normalizedPage;
    this.lastPageViewAt = now;

    if (!this.shouldSendEvent()) {
      return;
    }

    fetch(this.apiEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        visitorId: this.visitorId,
        sessionId: this.sessionId,
        page: normalizedPage,
        referrer: document.referrer || null,
        screenSize: `${window.innerWidth}x${window.innerHeight}`
      }),
      keepalive: true
    }).catch(err =>
      log.error(
        `[VisitorTrackingService:trackPageView] > [Request]: POST /visitors/track > [Response]: error=${err?.message || "unknown"} > [Comment]: page=${normalizedPage}`
      )
    );
  }

  sendHeartbeat() {
    if (!this.shouldSendEvent()) {
      return;
    }

    fetch(this.apiEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        visitorId: this.visitorId,
        sessionId: this.sessionId,
        page: this.currentPage,
        heartbeat: true
      })
    }).catch(err =>
      log.error(
        `[VisitorTrackingService:sendHeartbeat] > [Request]: POST /visitors/track > [Response]: error=${err?.message || "unknown"} > [Comment]: page=${this.currentPage}`
      )
    );
  }

  handleBeforeUnload() {
    if (!navigator?.sendBeacon) {
      return;
    }

    const endTime = new Date().toISOString();
    const payload = new Blob(
      [
        JSON.stringify({
          visitorId: this.visitorId,
          sessionId: this.sessionId,
          page: this.currentPage,
          previousSessionEnd: endTime
        })
      ],
      { type: "application/json" }
    );

    navigator.sendBeacon(this.apiEndpoint, payload);
  }
}

// Create singleton instance
const visitorTracking = new VisitorTrackingService();

export default visitorTracking;
